import {
  Mail, Shield, MoreHorizontal, Ban, Power, KeyRound,
  Send, Pencil, Building2
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  USER_ROLE_LABELS,
  USER_STATUS_COLORS,
  USER_STATUS_LABELS,
  type User,
} from '@/features/settings';

interface UserRowProps {
  readonly user: User;
  readonly onEditUser: () => void;
  readonly onAction: (userId: string, action: 'disable' | 'enable' | 'reset-password' | 'resend-invitation' | 'delete') => void;
  readonly supplierName?: string;
}

export function UserRow({
  user, onEditUser, onAction, supplierName 
}: UserRowProps) {
  return (
    <button
      type="button"
      className="flex w-full items-center justify-between p-4 hover:bg-muted/50 transition-colors cursor-pointer text-left"
      onClick={onEditUser}
    >
      <div className="flex items-center gap-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary font-medium">
          {user.firstName[0]}{user.lastName[0]}
        </div>
        <div>
          <p className="font-medium">{user.firstName} {user.lastName}</p>
          <div className="flex items-center gap-3 text-sm text-muted-foreground">
            <span className="flex items-center gap-1">
              <Mail className="h-3 w-3" /> {user.email}
            </span>
            {supplierName && (
              <span className="flex items-center gap-1">
                <Building2 className="h-3 w-3" /> {supplierName}
              </span>
            )}
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-4">
        <div className="text-right">
          <div className="flex items-center gap-1 text-sm">
            <Shield className="h-3 w-3" /> {USER_ROLE_LABELS[user.role]}
          </div>
          {user.jobTitle && (
            <p className="text-xs text-muted-foreground">{user.jobTitle}</p>
          )}
        </div>
        
        <span className={`rounded-full px-2 py-1 text-xs font-medium ${USER_STATUS_COLORS[user.status]}`}>
          {USER_STATUS_LABELS[user.status]}
        </span>
        
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" onClick={(e) => e.stopPropagation()}>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={onEditUser}>
              <Pencil className="mr-2 h-4 w-4" />
              Edit User
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {user.status === 'pending' && (
              <DropdownMenuItem onClick={() => onAction(user.userId, 'resend-invitation')}>
                <Send className="mr-2 h-4 w-4" />
                Resend Invitation
              </DropdownMenuItem>
            )}
            {user.status === 'active' && (
              <DropdownMenuItem onClick={() => onAction(user.userId, 'reset-password')}>
                <KeyRound className="mr-2 h-4 w-4" />
                Reset Password
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {user.status === 'disabled' ? (
              <DropdownMenuItem onClick={() => onAction(user.userId, 'enable')}>
                <Power className="mr-2 h-4 w-4" />
                Enable User
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem 
                onClick={() => onAction(user.userId, 'disable')}
                className="text-destructive focus:text-destructive"
              >
                <Ban className="mr-2 h-4 w-4" />
                Disable User
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </button>
  );
}
